import type {
  LabelFilterListType,
  LabelFilterType,
  FilterOptionType,
} from 'src/designSystem2021Components/types/label-filter';
import type {OptionProps} from 'src/designSystem2021Components/menu/options';

import {
  Options,
  CheckboxOption,
  RadioOption,
} from 'src/designSystem2021Components/menu/options';

import * as React from 'react';

import {classify} from 'src/utils/classify';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';

import {LabelFilterComponent} from './filter-label';
import {FilterContainer} from './container/filter-container';

import css from 'src/designSystem2021Components/label-filter/label-filter.css';


export type LabelFilterProps<V> = {
  ContainerClassName?: string;
  optionClassName?: string;
  optionsClassName?: string;
  listType?: LabelFilterListType;
  filterType?: LabelFilterType;
  text?: string;
  options?: V[];
  onChange?: (item: string[]) => unknown;
  onClick?: () => unknown;
  resolveLabel?: (arg1: V) => string;
  resolveValue?: (arg1: V) => string;
  resolveDisabled?: (arg1: V, arg2: V[]) => boolean;
  value: string[] | string;
  disabled?: boolean;
  size?: 'standard' | 'small';
  anchorPosition?: 'left' | 'right';
  className?: string;
  OptionComponent?: React.ComponentType<OptionProps<V>>;
};

/**
 * an label that allows selecting a value from a limited set of options.
 *
 * @param  props.listType  the controlled type and it has two selection checkbox || radio
 * @param  props.onChange  the handler that is fired when the user tries to change the value and have selected Item
 * @param  props.onClick   the handle when filterType is panel
 * @param  props.value  this list will ensure pre selected value from option should be value of list
 * @param  props.filterType  label filter have two type panel and menu
 * @param  props.text  By Default text which will be visible for the user
 * @param  props.ContainerClassName  a custom className which will apply on filter label not list
 * @param  props.options  total list of allowed values for this field. will be used to provide suggestions.
 */

export const LabelFilter = <
  V extends
    | {
        label: string;
        value: string;
      }
    | {
        label: string;
        value: string;
        disabled?: boolean;
      },
>({
  className,
  ContainerClassName,
  optionClassName,
  optionsClassName,
  filterType = 'menu',
  listType = 'checkbox',
  text,
  size,
  options = [],
  disabled,
  value,
  onChange = () => [],
  onClick = () => {},
  resolveLabel = (item: V) => item.label,
  resolveValue = (item: V) => item.value,

  resolveDisabled = (item: V, _: Array<V>) =>
    // @ts-expect-error - TS2339 - Property 'disabled' does not exist on type '{ label: string; value: string; } | { label: string; value: string; disabled?: boolean | undefined; }'. | TS2339 - Property 'disabled' does not exist on type '{ label: string; value: string; } | { label: string; value: string; disabled?: boolean | undefined; }'.
    typeof item.disabled === 'boolean' ? item.disabled : false,

  anchorPosition = 'left',
  OptionComponent,
}: LabelFilterProps<V>) => {
  const items: string[] = Array.isArray(value) ? value : [];

  const handleOnSelect = (data: V) => {
    if (items.includes(resolveValue(data))) {
      onChange(items.filter((item) => item !== resolveValue(data)));
    } else {
      onChange([...items, resolveValue(data)]);
    }
  };

  return (
    <ClickAway>
      {({isOpen, onOpen, triggerRef, boundaryRef}) => (
        <div className={classify(css.filter, className)}>
          {/* @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'. */}
          <div ref={triggerRef}>
            <LabelFilterComponent
              className={ContainerClassName}
              size={size}
              text={text}
              activeFilters={items.length}
              onClick={(e) => {
                filterType === 'menu' ? onOpen() : onClick();
              }}
              isOpen={isOpen}
              disabled={disabled}
              filterType={filterType}
            />
          </div>
          {!disabled && (
            <div
              className={classify(
                css.filterCard,
                {
                  [css.smallFilterCard]: size === 'small',
                  [css.left]: anchorPosition === 'left',
                  [css.right]: anchorPosition === 'right',
                },
                isOpen ? css.open : css.close,
              )}
              ref={boundaryRef}
            >
              {filterType === 'menu' && options.length > 0 ? (
                <Options
                  options={options}
                  value={value}
                  resolveValue={resolveValue}
                  resolveLabel={resolveLabel}
                  resolveDisabled={resolveDisabled}
                  classNames={{option: optionClassName}}
                  onSelect={handleOnSelect}
                  OptionComponent={CheckboxOption}
                  // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
                  classNames={{
                    options: optionsClassName,
                    option: optionClassName,
                  }}
                />
              ) : null}
            </div>
          )}
        </div>
      )}
    </ClickAway>
  );
};
